/**
 * Extracts all unique name properties from a nested data structure recursively.
 *
 * This function traverses through objects and arrays to find all string-valued 'name'
 * properties, excluding those where the object has type "LABEL". It also skips traversing
 * into certain object properties ('inputParameters', 'subWorkflowParam', 'startWorkflow').
 *
 * @param {Object|Array} data - The data structure to extract names from
 * @returns {Array<string>} - Array of unique name values found in the data structure
 */
export function extractNamesRecursively(data) {
  const names = new Set();

  function traverse(item) {
    if (!item || (typeof item !== "object" && !Array.isArray(item))) {
      return;
    }

    if (Array.isArray(item)) {
      item.forEach(traverse);
    } else {
      if (
        "name" in item &&
        typeof item.name === "string" &&
        item.type !== "LABEL"
      ) {
        names.add(item.name);
      }

      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          if (
            ["inputParameters", "subWorkflowParam", "startWorkflow"].includes(
              key
            )
          )
            continue;
          traverse(item[key]);
        }
      }
    }
  }

  traverse(data);
  return Array.from(names);
}

export function extractSubWorkflows(workflowJson) {
  const foundSubWorkflows = [];

  // Recursive helper function to traverse any object or array
  function findRecursively(node) {
    // Base case: if node is null or not an object/array, stop
    if (!node || typeof node !== "object") {
      return;
    }

    // 1. CHECK: Is the current node a SUB_WORKFLOW task?
    if (node.type === "SUB_WORKFLOW" && node.subWorkflowParam) {
      foundSubWorkflows.push({
        name: node.subWorkflowParam.name,
        version: node.subWorkflowParam.version,
        taskReferenceName: node.taskReferenceName,
        taskName: node.name,
      });
    }

    // 2. TRAVERSE: Dig deeper into the structure
    if (Array.isArray(node)) {
      // If it's an array, iterate over every item
      node.forEach((item) => findRecursively(item));
    } else {
      // If it's an object, iterate over every value in the object keys
      // This ensures we look inside 'decisionCases', 'loopOver', etc.
      Object.values(node).forEach((value) => findRecursively(value));
    }
  }

  // Entry Point: Only start the recursion if 'tasks' exists
  if (workflowJson && Array.isArray(workflowJson.tasks)) {
    findRecursively(workflowJson.tasks);
  }

  return foundSubWorkflows;
}

export function extractStartWorkflows(workflowJson) {
  const foundStartWorkflows = [];

  // Recursive helper function to traverse any object or array
  function findRecursively(node) {
    // Base case: if node is null or not an object/array, stop
    if (!node || typeof node !== "object") {
      return;
    }

    // 1. CHECK: Is the current node a START_WORKFLOW task?
    if (
      node.type === "START_WORKFLOW" &&
      node.inputParameters &&
      node.inputParameters.startWorkflow
    ) {
      foundStartWorkflows.push({
        name: node.inputParameters.startWorkflow.name,
        version: node.inputParameters.startWorkflow.version,
        taskReferenceName: node.taskReferenceName,
        taskName: node.name,
      });
    }

    // 2. TRAVERSE: Dig deeper into the structure
    if (Array.isArray(node)) {
      // If it's an array, iterate over every item
      node.forEach((item) => findRecursively(item));
    } else {
      // If it's an object, iterate over every value in the object keys
      // This ensures we look inside 'decisionCases', 'loopOver', etc.
      Object.values(node).forEach((value) => findRecursively(value));
    }
  }

  // Entry Point: Only start the recursion if 'tasks' exists
  if (workflowJson && Array.isArray(workflowJson.tasks)) {
    findRecursively(workflowJson.tasks);
  }

  return foundStartWorkflows;
}
